/**
 * 資料エディタ 右パネル — ライブプレビュー。
 * viewport (desktop/tablet/mobile) は max-width で切替。
 */
import type { CSSProperties } from 'react';

import type { DocumentRecord } from '@/hooks/useDocuments';

import { DocumentSectionRenderer } from './DocumentSectionRenderer';

interface Props {
  document: DocumentRecord;
  activeSectionId: string | null;
  viewport: 'desktop' | 'tablet' | 'mobile';
  onSelectSection: (id: string) => void;
}

export function DocumentPreviewCanvas({
  document,
  activeSectionId,
  viewport,
  onSelectSection,
}: Props) {
  const sections = document.sections ?? [];
  const widthMap = { desktop: 1280, tablet: 768, mobile: 375 } as const;
  const targetWidth = widthMap[viewport];

  if (sections.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center gap-3 p-8">
        <div className="text-4xl opacity-30">📄</div>
        <h2 className="text-base font-semibold">プレビューはまだありません</h2>
        <p className="text-sm text-[var(--text-muted)] max-w-md">
          中央パネルの「+ セクションを追加」から
          <br />
          ヒーロー / 比較表 / CTA などを追加できます。
        </p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div
        className="mx-auto rounded-lg border border-[var(--border)] overflow-hidden bg-white dark:bg-[#0B0D10] shadow-lg"
        style={{
          maxWidth: targetWidth,
          width: '100%',
          transition: 'max-width 0.2s',
        }}
      >
        <div className="flex items-center px-3 py-2 bg-[#f5f5f3] dark:bg-[#1A1E25] border-b border-[var(--border)]">
          <div className="flex gap-1.5">
            <span className="size-3 rounded-full bg-red-400" />
            <span className="size-3 rounded-full bg-yellow-400" />
            <span className="size-3 rounded-full bg-green-400" />
          </div>
          <div className="flex-1 text-center text-xs text-gray-500 font-mono">
            /docs/{document.slug || '(slug 未設定)'}
          </div>
          <div className="text-xs text-gray-400">{targetWidth}px</div>
        </div>
        <div data-doc-theme={document.theme ?? 'light'}>
          <div className="max-w-3xl mx-auto p-8 space-y-8">
            <h1 className="text-3xl font-bold">{document.title}</h1>
            {sections.map((s) => {
              const isActive = s.id === activeSectionId;
              const isHidden = !!s.hidden;
              const baseStyle: CSSProperties = {
                opacity: isHidden ? 0.4 : 1,
                transition: 'opacity 0.2s',
              };
              if (isActive) {
                baseStyle.outline = '3px solid var(--accent, #3D5AFE)';
                baseStyle.outlineOffset = -3;
              }
              return (
                <div
                  key={s.id}
                  className="relative cursor-pointer"
                  onClick={() => onSelectSection(s.id)}
                  style={baseStyle}
                >
                  {isActive && (
                    <div
                      className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded text-[10px] font-semibold bg-[var(--accent)] text-white"
                      style={{ pointerEvents: 'none' }}
                    >
                      {s.type}
                    </div>
                  )}
                  {isHidden && (
                    <div
                      className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-700 text-white"
                      style={{ pointerEvents: 'none' }}
                    >
                      非表示
                    </div>
                  )}
                  <DocumentSectionRenderer section={s} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
